import type { ReactElement } from 'react';

/**
 * Two-state primary action: "Start call" / "End call".
 *
 * Replaces the press-to-talk MicButton on the embed page. Once the
 * call is live, the user does NOT click again to commit utterances —
 * the upstream ASR + server-side VAD detects end-of-utterance and the
 * relay automatically pipelines reply LLM + TTS back. Click ONLY to
 * start or to end the entire call.
 *
 * Visual: dark + translucent + glassmorphism pill. Sits in a unified
 * bottom-center control bar (alongside the text-mode toggle) so the
 * embed page has one clear focal point for input controls — no
 * top-right toggle off in a corner.
 */

export interface CallButtonProps {
  live: boolean;
  busy?: boolean;
  onStart: () => void;
  onStop: () => void;
}

export function CallButton({ live, busy, onStart, onStop }: CallButtonProps): ReactElement {
  return (
    <button
      type="button"
      onClick={() => (live ? onStop() : onStart())}
      disabled={busy}
      className={
        live
          // Live state: red glow + pulsing dot inside dark glass shell.
          // `whitespace-nowrap` prevents the label from wrapping into
          // two lines when the embed sits inside the loader's compact
          // 300px panel — single-line label only.
          ? 'group inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 ' +
            'bg-rose-600/25 ring-1 ring-rose-400/50 backdrop-blur-md ' +
            'text-rose-100 font-medium text-xs tracking-wide whitespace-nowrap shadow-[0_0_20px_-4px_rgba(244,63,94,0.5)] ' +
            'hover:bg-rose-600/35 hover:ring-rose-400/70 transition disabled:opacity-50'
          // Idle state: muted graphite glass.
          : 'group inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 ' +
            'bg-slate-900/60 ring-1 ring-white/15 backdrop-blur-md ' +
            'text-slate-100 font-medium text-xs tracking-wide whitespace-nowrap shadow-lg ' +
            'hover:bg-slate-800/70 hover:ring-white/25 transition disabled:opacity-50'
      }
    >
      {live ? (
        <>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-300" />
          </span>
          <span>結束通話</span>
        </>
      ) : (
        <>
          <svg className="h-3.5 w-3.5 text-blue-300" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.04 15.04 0 0 1-6.59-6.59l2.2-2.21a1 1 0 0 0 .25-1.02A11.36 11.36 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1c0 9.39 7.61 17 17 17a1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z" />
          </svg>
          <span>通話</span>
        </>
      )}
    </button>
  );
}
